import React, { useEffect, useState } from 'react'
import { getAuthToken, refreshToken } from 'lib/auth'
import { useLocation } from 'react-router-dom'
import { bindActionCreators, Dispatch } from 'redux'
import { Route } from 'react-router'
import { connect } from 'react-redux'
import history from 'utils/history'
import { ROLES } from 'config'
import { IdleTrackingContainer } from 'pages/Common/container'
import {
  getUserProfileAction,
  setTenantIdAction,
  updateUserProfileAction,
  getUnreadNotificationsAction,
} from 'store/actions/user'
import isEmpty from 'lodash/isEmpty'
import { logoutAction } from 'store/actions/auth'
import { getCurrentRoute } from 'utils/helper'
import redirectTo from 'utils/redirectTo'
import moment from 'moment-timezone'
import { getBSOProfileAction } from 'store/actions/bso'
import { RootState } from 'App'
import Loader from 'components/Loaders'
import { AppFloatingChat } from 'components/Common/AppFloatingChat/AppFloatingChat.component'
import { useFeatureFlag } from 'hooks/userFeatureFlag'

/**
 * PrivateRoute is used to support the react router and it renders the routes
 * which is marked as private or is only accessible authenticated users
 * @param {React.Component} component
 */

const setTimezone = (timezone: string) => {
  moment.tz.setDefault(timezone ?? moment.tz.guess())
}

const PrivateRoute = ({
  user,
  component,
  userTenantId,
  logoutAction,
  getUserProfileAction,
  isUpdateProfileLoading,
  updateUserProfileAction,
  setTenantIdAction,
  getUnreadNotificationsAction,
  getBSOProfileAction,
  isBsoProfileExist,
  isV2,
  ...rest
}) => {
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const isFloatingChatEnabled = useFeatureFlag(user?.id, 'release-floating-chat')

  useEffect(() => {
    getUnreadNotificationsAction()
    // eslint-disable-next-line
  }, [window.location.href])

  useEffect(() => {
    ;(async () => {
      const jwtToken = await getAuthToken()
      if (!jwtToken) {
        const updatedSession = await refreshToken()
        if (!updatedSession.isValid()) {
          redirectTo('/auth/login', getCurrentRoute())
          return
        }
      }
    })()
  }, [])

  useEffect(() => {
    setLoading(true)
    if (isEmpty(user?.id)) {
      getUserProfileAction()
    } else {
      let found = false
      const roles = user.roles
      const pathName = location.pathname
      let tenantId = pathName?.split('/')[2]
      roles.forEach((role) => {
        const words = pathName.split('/')
        if (words.includes(ROLES[role]) || words.includes(ROLES.V2)) {
          found = true
        }
      })
      if (isEmpty(userTenantId)) {
        tenantId = tenantId ? tenantId : user?.tenantId[0]
        setTenantIdAction(tenantId)
      }

      // get bso profile
      if (!isBsoProfileExist) {
        getBSOProfileAction(tenantId)
      }

      if (!user?.timezone) {
        updateUserProfileAction({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      }

      if (!found) {
        history.goBack()
      }
      setLoading(false)
      setTimezone(user?.timezone)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const routeComponent = (props) => React.createElement(component, props)

  return (
    <>
      {!loading ? (
        <>
          <IdleTrackingContainer />
          <Route {...rest} render={routeComponent} />
          {isFloatingChatEnabled && <AppFloatingChat sessionId={userTenantId} />}
        </>
      ) : (
        <Loader loader="LogoLoader" />
      )}
    </>
  )
}

const mapProps = (state: RootState) => {
  const user = state.user
  const isBsoProfileExist = Boolean(state.bso.bsoProfile.id ?? '')
  return {
    user: user?.user,
    userTenantId: user?.tenantId,
    isUpdateProfileLoading: state?.user?.isUpdateProfileLoading,
    isBsoProfileExist,
  }
}

function mapDispatch(dispatch: Dispatch) {
  return bindActionCreators(
    {
      getUserProfileAction: getUserProfileAction.STARTED,
      getUnreadNotificationsAction: getUnreadNotificationsAction.STARTED,
      setTenantIdAction: setTenantIdAction,
      logoutAction: logoutAction,
      updateUserProfileAction: updateUserProfileAction.STARTED,
      getBSOProfileAction: getBSOProfileAction.STARTED,
    },
    dispatch
  )
}

export default connect(mapProps, mapDispatch)(PrivateRoute)
